/**
 * IDT 看板「デザイン評価」カードの軸定義・配色・期間 (月 / FY) ユーティリティ。
 *
 * 案件開始時の解像度 5 軸 (企画 3 + 設計 2) と、チーム評価 3 軸 (貢献度) の表示順・色を
 * SSOT として保持する。radar (前月比較) と推移折線で共有する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン評価カードの軸・配色・FY/月期間ヘルパー
 * @graph-connects none
 */

import type { IdtDesignEvaluationPoint } from '../../../lib/api.js';

/**
 * 月 / Q 共通の評価系列点。period は月 ("YYYY-MM") か Q ("YYYY/NQ")。
 * radar / 折線はこの汎用型で月・四半期どちらも描画する。
 * @graph-connects none
 */
export interface EvalSeriesPoint {
  period: string;
  resolution: IdtDesignEvaluationPoint['resolution'];
  evaluation: IdtDesignEvaluationPoint['evaluation'];
  /** チーム貢献度評価の評価対象件数 (radar 横の件数表示用) */
  evaluationCounts: IdtDesignEvaluationPoint['evaluationCounts'];
}

/** 解像度軸のキー (resolution の field 名)。 @graph-connects none */
export type ResolutionAxisKey = keyof IdtDesignEvaluationPoint['resolution'];
/** 評価軸のキー (evaluation の field 名)。 @graph-connects none */
export type EvaluationAxisKey = keyof IdtDesignEvaluationPoint['evaluation'];

/** 軸の分類 (解像度軸の企画 / 設計)。radar の軸ラベル着色に使う。 @graph-connects none */
export type AxisCategory = 'plan' | 'design';

/** 軸定義 (キー + 折線色 + i18n ラベルキー)。 @graph-connects none */
export interface AxisDef<K extends string> {
  key: K;
  /** 折線モードの線色 */
  color: string;
  /** i18n ラベルキー (idtKpi.designEvaluation.axes.<key>) */
  labelKey: string;
  /** 軸の分類 (解像度のみ。radar 軸ラベルを企画=青 / 設計=桃 で色分けする) */
  category?: AxisCategory;
}

/**
 * 案件開始時の解像度 5 軸。折線は「浅 → 深」の青緑 (teal) ランプ
 * (課題・ゴール設定=最浅 … 情報設計=最深)。radar の当月色 (teal) と色系統を揃える。
 * category は radar の軸ラベル着色用 (企画=青 / 設計=桃)。
 * @graph-connects none
 */
export const RESOLUTION_AXES: readonly AxisDef<ResolutionAxisKey>[] = [
  {
    key: 'issueGoal',
    color: '#99f6e4',
    labelKey: 'idtKpi.designEvaluation.axes.issueGoal',
    category: 'plan',
  },
  {
    key: 'touchpoint',
    color: '#5eead4',
    labelKey: 'idtKpi.designEvaluation.axes.touchpoint',
    category: 'plan',
  },
  {
    key: 'howDraft',
    color: '#2dd4bf',
    labelKey: 'idtKpi.designEvaluation.axes.howDraft',
    category: 'plan',
  },
  {
    key: 'roughDetail',
    color: '#14b8a6',
    labelKey: 'idtKpi.designEvaluation.axes.roughDetail',
    category: 'design',
  },
  {
    key: 'infoDesign',
    color: '#0f766e',
    labelKey: 'idtKpi.designEvaluation.axes.infoDesign',
    category: 'design',
  },
] as const;

/** チーム評価 3 軸 (貢献度)。折線は軸ごとに異なる色相。 @graph-connects none */
export const EVALUATION_AXES: readonly AxisDef<EvaluationAxisKey>[] = [
  { key: 'planning', color: '#f59e0b', labelKey: 'idtKpi.designEvaluation.axes.planning' },
  { key: 'design', color: '#10b981', labelKey: 'idtKpi.designEvaluation.axes.design' },
  { key: 'visual', color: '#ec4899', labelKey: 'idtKpi.designEvaluation.axes.visual' },
] as const;

/** PJ解像度 radar の当月 (選択月) 系列色 (青緑 / teal)。 @graph-connects none */
export const RESOLUTION_RADAR_COLOR = '#14b8a6';
/** チーム貢献度評価 radar の当月 (選択月) 系列色 (橙)。 @graph-connects none */
export const EVALUATION_RADAR_COLOR = '#f97316';
/**
 * チーム貢献度評価 radar の「前期間の個人評価」参照系列色 (橙の淡色)。
 * 個人 MyPage で当期間 (橙) との対比により前月比の伸びを示すために使う。
 * @graph-connects none
 */
export const EVALUATION_RADAR_SELF_PREV_COLOR = '#fdba74';
/** radar の前月ベースライン色 (グレー)。 @graph-connects none */
export const RADAR_PREV_COLOR = '#9ca3af';

/** 解像度軸ラベルの分類色 (企画=青 / 設計=桃)。 @graph-connects none */
export const CATEGORY_COLORS: Record<AxisCategory, string> = {
  plan: '#60a5fa',
  design: '#f472b6',
};

/** 解像度軸の点数スケール (満点 3)。radar の半径に使う。 @graph-connects none */
export const RESOLUTION_RADIUS_DOMAIN: [number, number] = [0, 3];
/** 貢献度評価軸の点数スケール (満点 5)。radar の半径に使う。 @graph-connects none */
export const EVALUATION_RADIUS_DOMAIN: [number, number] = [0, 5];

// 折線図 y 軸は実データがほぼ使わない低分域を省略し、実際の評価帯を強調する
// (どの履歴値もこの下限を下回らないので裁切は起きない)。
/** 解像度 折線 y 軸 (0〜1 を省略)。 @graph-connects none */
export const RESOLUTION_TREND_DOMAIN: [number, number] = [1, 3];
/** 貢献度 折線 y 軸 (0〜2 を省略)。 @graph-connects none */
export const EVALUATION_TREND_DOMAIN: [number, number] = [2, 5];

/**
 * "YYYY-MM" の属する FY 開始年 (7 月始まり会計年度)。例: 2026-06 → 2025 / 2026-07 → 2026。
 * @graph-connects none
 */
export function fyStartYearOf(month: string): number {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return 0;
  const year = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  return mo >= 7 ? year : year - 1;
}

/**
 * FY 開始年 → その FY の 12 ヶ月 ("YYYY-MM", 7 月始まり〜翌 6 月) を昇順で返す。
 * @graph-connects none
 */
export function fyMonths(startYear: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < 12; i++) {
    const raw = 7 + i; // 7..18
    const year = raw <= 12 ? startYear : startYear + 1;
    const mo = raw <= 12 ? raw : raw - 12;
    out.push(`${String(year)}-${String(mo).padStart(2, '0')}`);
  }
  return out;
}

/** FY 開始年 → 表示ラベル (例: "2025/7〜2026/6")。 @graph-connects none */
export function fyLabelOf(startYear: number): string {
  return `${String(startYear)}/7〜${String(startYear + 1)}/6`;
}
